import { useContext, useEffect, useMemo } from "react";

import { Button, DatePicker } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useIsFetching } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AppContext from "../../context/AppContext";
import { SIDER_TABS } from "../../utils/Constants";
import {
  NAVIGATION_ROUTES,
  buildReportingSummaryProfitabilityPath,
} from "../../utils/NavigationUtils";
import { AppStrings } from "../../utils/constants/AppStrings";
import { reportingSummaryFiltersFromSearchParams } from "../../queries/sportsbook/reportingSummaryFilterOptions";
import { PROFITABILITY_DETAILS_QUERY_KEY } from "../../queries/sportsbook/profitabilityDetails";
import { SPORTSBOOK_ANALYTICS_SUMMARY_QUERY_KEY } from "../../queries/sportsbook/useSportsbookAnalyticsSummary";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";
import {
  ProfitabilitySportsSection,
  ProfitabilitySummarySection,
  ProfitabilityTopEventsSection,
} from "./ProfitabilitySections";

import "./stylesReportingSummary.css";

dayjs.extend(customParseFormat);

function ReportingSummaryProfitability() {
  const { date } = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setActiveSidebarTab } = useContext(AppContext) ?? {};

  useEffect(() => {
    setActiveSidebarTab?.(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_REPORTING_SUMMARY");
  }, [setActiveSidebarTab]);

  const parsedDate = useMemo(() => dayjs(date, "YYYY-MM-DD", true), [date]);
  const isoDate = parsedDate.isValid()
    ? parsedDate.format("YYYY-MM-DD")
    : "";
  const detailsFetching = useIsFetching({
    queryKey: [PROFITABILITY_DETAILS_QUERY_KEY],
  });
  const summaryFetching = useIsFetching({
    queryKey: [SPORTSBOOK_ANALYTICS_SUMMARY_QUERY_KEY, isoDate, isoDate],
  });
  const isDataFetching = detailsFetching + summaryFetching > 0;
  const inheritedFilters = useMemo(
    () => reportingSummaryFiltersFromSearchParams(searchParams),
    [searchParams],
  );
  const pageTitle = useMemo(() => {
    if (!parsedDate.isValid()) {
      return AppStrings.REPORTING_PROFITABILITY_REPORTS;
    }

    return `${AppStrings.REPORTING_PROFITABILITY_REPORTS} (${parsedDate.format(
      "MM/DD/YY",
    )})`;
  }, [parsedDate]);
  usePageTitle(pageTitle);

  const handleDateChange = (nextDate: Dayjs | null) => {
    if (!nextDate) return;
    const search = searchParams.toString();
    navigate(
      {
        pathname: buildReportingSummaryProfitabilityPath(
          nextDate.format("YYYY-MM-DD"),
        ),
        search: search ? `?${search}` : "",
      },
      { replace: true },
    );
  };

  return (
    <>
      <div className="reporting-summary-page reporting-summary-profitability-page">
        <div className="reporting-summary-profitability-top">
          <Button
            type="default"
            icon={<LeftOutlined />}
            aria-label={AppStrings.BACK}
            className="reporting-summary-profitability-back-icon"
            onClick={() => navigate(NAVIGATION_ROUTES.REPORTING_SUMMARY)}
          />
          <DatePicker
            value={parsedDate.isValid() ? parsedDate : null}
            format="MM/DD/YY"
            allowClear={false}
            aria-label={AppStrings.DATE}
            className="reporting-summary-profitability-date-picker"
            onChange={handleDateChange}
            disabled={isDataFetching}
            showNow={false}
          />
        </div>
        <ProfitabilitySummarySection
          date={isoDate}
          inheritedFilters={inheritedFilters}
        />
        <ProfitabilitySportsSection
          date={isoDate}
          inheritedFilters={inheritedFilters}
        />
        <ProfitabilityTopEventsSection
          date={isoDate}
          inheritedFilters={inheritedFilters}
        />
      </div>
    </>
  );
}

export default ReportingSummaryProfitability;
